import api from './api';

const POPUP_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns the backend base URL without a trailing slash.
 */
function getBackendBase() {
  return (process.env.REACT_APP_API_LOCAL_URL || '').replace(/\/$/, '');
}

class BlingOAuthService {
  /**
   * Opens an empty popup while the browser still considers the call a user gesture.
   * The OAuth URL is only assigned later, after the caller has confirmed that the
   * Sync exists in the backend.
   */
  static openPopup(popupNameSuffix = 'pending') {
    const top = Math.floor((window.screen.height / 2) - 350);
    const left = Math.floor((window.screen.width / 2) - 300);
    const popupName = `BlingOAuth_${popupNameSuffix}_${Date.now()}`;

    const popup = window.open(
      'about:blank',
      popupName,
      `width=600,height=700,popup=1,top=${top},left=${left}`,
    );

    if (!popup) {
      throw new Error(
        'Não foi possível abrir o popup de autorização. ' +
        'Verifique se popups estão bloqueados no seu navegador.',
      );
    }

    return popup;
  }

  /**
   * Executes the backend-centralised Bling OAuth flow:
   *
   * 1. Opens a popup (must be synchronous, inside user-gesture context).
   * 2. Calls POST /oauth/bling/launch (authenticated) to get a backend launch token.
   * 3. Navigates the popup to GET /oauth/bling/start?t={token} on the Backend.
   *    Backend redirects popup → Integrador start → Bling authorization page.
   * 4. After user authorizes, Bling redirects to Integrador callback.
   *    Integrador exchanges code, marks sync as configured, posts message to opener.
   * 5. Resolves with true on success; rejects with Error on failure.
   *
   * Security: ServiceAuth:ApiKey never reaches the browser.
   *           The Backend uses the "Bling" named HttpClient (Bearer token) internally.
   */
  static async completeAuth(syncId, existingPopup = null) {
    // Open popup synchronously BEFORE any async call so it stays inside the
    // user-gesture context. Browsers block window.open() called after an await.
    const popup = existingPopup || this.openPopup(syncId);

    // Step 1: obtain short-lived backend launch token (requires valid JWT)
    let launchToken;
    try {
      const launchResponse = await api.post('/oauth/bling/launch', { syncId });
      if (launchResponse.status !== 200) {
        popup.close();
        throw new Error(`Erro ao obter token de launch: status ${launchResponse.status}`);
      }
      launchToken = launchResponse.data?.launchToken;
      if (!launchToken) {
        popup.close();
        throw new Error('Launch token não recebido do servidor.');
      }
    } catch (err) {
      try { popup.close(); } catch (_) { /* ignore */ }
      throw err;
    }

    const backendBase = getBackendBase();
    const popupUrl = `${backendBase}/oauth/bling/start?t=${encodeURIComponent(launchToken)}`;

    // Step 2: navigate the already-open popup to the backend OAuth start URL
    popup.location.href = popupUrl;

    // Step 3: wait for postMessage from Integrador callback page
    return new Promise((resolve, reject) => {
      let settled = false;

      function cleanup() {
        clearInterval(closedInterval);
        clearTimeout(timeoutHandle);
        window.removeEventListener('message', onMessage);
        settled = true;
      }

      function onMessage(event) {
        const { type, success, error } = event.data || {};
        if (type !== 'bling-oauth') return;

        cleanup();
        if (success) {
          resolve(true);
        } else {
          reject(new Error(error || 'Autenticação com Bling falhou.'));
        }
      }

      // Detect popup closure. Keep listener alive for 500ms after closure so
      // any in-flight postMessage can still be dispatched.
      const closedInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedInterval);
          clearTimeout(timeoutHandle);
          setTimeout(() => {
            if (!settled) {
              window.removeEventListener('message', onMessage);
              settled = true;
              reject(new Error('Popup foi fechado pelo usuário antes de concluir a autorização.'));
            }
          }, 500);
        }
      }, 500);

      // Safety timeout
      const timeoutHandle = setTimeout(() => {
        cleanup();
        try { popup.close(); } catch (_) { /* ignore */ }
        reject(new Error('Timeout: a autorização OAuth2 não foi concluída em 5 minutos.'));
      }, POPUP_TIMEOUT_MS);

      window.addEventListener('message', onMessage);
    });
  }
}

export default BlingOAuthService;
